import React, { useState } from "react";
import { useQuery } from "react-query";
import { Col, Row, Typography, Select, DatePicker, Spin } from "antd";
import { getKpis } from "../../services/dashboard.service";
import { getAll as loadCustomers } from "../../services/customers.service";
import { getAll as loadCategories } from "../../services/categories.service";
import { formatCurrency } from "../../services/utils";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import locale from "antd/es/date-picker/locale/pt_BR";
import KpiCard from "./components/KpiCard";
import RevenueExpenseChart from "./components/RevenueExpenseChart";
import CollectionsStatus from "./components/CollectionsStatus";
import ExpensesBreakdown from "./components/ExpensesBreakdown";
import OverdueList from "./components/OverdueList";
import {
  BankOutlined,
  ClockCircleOutlined,
  WalletOutlined,
  DollarOutlined,
} from "@ant-design/icons";
dayjs.locale("pt-br");

const { Title } = Typography;

export default function Dashboard() {
  const [period, setPeriod] = useState<dayjs.Dayjs | null>(dayjs());
  const [customerId, setCustomerId] = useState<number | undefined>(undefined);
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);

  // Filters object
  const params = {
    start_date: period ? period.startOf('month').format("YYYY-MM-DD") : undefined,
    end_date: period ? period.endOf('month').format("YYYY-MM-DD") : undefined,
    customer_id: customerId,
    category_id: categoryId,
  };

  // Load KPIs
  const { data: kpisResponse, isLoading: kpisLoading } = useQuery(
    ["loadKpis", params],
    () => getKpis(params),
    { refetchOnWindowFocus: false }
  );

  const kpis = kpisResponse?.data || {
    financials: {
      total_revenue: 0,
      total_expenses: 0,
      balance: 0,
      result_status: "positive",
    },
    collections: {
      to_receive: 0,
      overdue: { count: 0, amount: 0 },
      received: 0,
    },
    efficiency: { default_rate: 0, average_ticket: 0, average_payment_time: 0 },
    expenses: {
      top_category: { name: "", value: 0 },
      breakdown: { fixed: 0, variable: 0 },
    },
  };

  // Load Filters Data
  const { data: customersResponse } = useQuery(
    ["loadCustomers"],
    () => loadCustomers({ status: "active" }),
    {
      refetchOnWindowFocus: false,
    }
  );

  const customers =
    customersResponse?.data?.map((c: any) => ({
      value: c.data.id,
      label: c.data.name,
    })) || [];

  const { data: categoriesResponse } = useQuery(
    ["loadCategories"],
    () => loadCategories({ status: "active" }),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Assuming categories follow similar structure, if not, it might fail, but usually consistent.
  // If categories are simple array, we might need adjustment.
  // But typically in this project "getAll" returns wrapped resources.
  const categories =
    categoriesResponse?.data?.map((c: any) => ({
      value: c.data.id,
      label: c.data.name,
    })) || [];

  return (
    <div style={{ padding: 24 }}>
      {/* Topo - Filtros Globais */}
      <div
        style={{
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Meu resumo
        </Title>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <DatePicker
            picker="month"
            value={period}
            onChange={(date) => setPeriod(date)}
            format="MMMM YYYY"
            allowClear={false}
            style={{ width: 150 }}
            locale={locale}
          />
          <Select
            placeholder="Cliente"
            style={{ width: 200 }}
            allowClear
            showSearch
            optionFilterProp="children"
            onChange={(val) => setCustomerId(val)}
            filterOption={(input, option) =>
              String(option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            options={customers}
          />
          <Select
            placeholder="Categoria"
            style={{ width: 200 }}
            allowClear
            showSearch
            optionFilterProp="children"
            onChange={(val) => setCategoryId(val)}
            filterOption={(input, option) =>
              String(option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            options={categories}
          />
        </div>
      </div>

      {kpisLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* KPI Line */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <KpiCard
                title="Receitas"
                value={formatCurrency(kpis.financials.total_revenue)}
                icon={<DollarOutlined style={{ color: "#52c41a" }} />}
                color="#3f8600"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <KpiCard
                title="Despesas"
                value={formatCurrency(kpis.financials.total_expenses)}
                icon={<WalletOutlined style={{ color: "#cf1322" }} />}
                color="#cf1322"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <KpiCard
                title="Saldo"
                value={formatCurrency(kpis.financials.balance)}
                icon={<BankOutlined />}
                color={
                  kpis.financials.result_status === "positive"
                    ? "#3f8600"
                    : "#cf1322"
                }
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <KpiCard
                title="Em Atraso"
                value={formatCurrency(kpis.collections.overdue.amount)}
                icon={<ClockCircleOutlined style={{ color: "#faad14" }} />}
                color="#faad14"
                suffix={
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    ({kpis.collections.overdue.count})
                  </Typography.Text>
                }
              />
            </Col>
          </Row>

          {/* Main Chart */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <RevenueExpenseChart />
            </Col>
          </Row>

          {/* Secondary Charts */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12}>
              <ExpensesBreakdown
                fixed={kpis.expenses.breakdown.fixed}
                variable={kpis.expenses.breakdown.variable}
              />
            </Col>
            <Col xs={24} lg={12}>
              <CollectionsStatus
                received={kpis.collections.received}
                to_receive={kpis.collections.to_receive}
                overdue={kpis.collections.overdue.amount}
              />
            </Col>
          </Row>

          {/* List */}
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <OverdueList params={params} />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
